import { jsPDF } from 'jspdf';
import type { NodeEntry } from 'slate';

import { PDFLinks } from './links';
import { loadPDFFonts } from './load-fonts';
import { createPDFPage } from './page';
import { setPDFTextFont } from './utils';
import type { AlineFontName, DocParts, RenderModelPage } from '../../../slate-api';
import { AlineTransforms, buildRenderModel, Queries } from '../../../slate-api';
import type { ImageMap } from '../../../types';
import { arrayEntries } from '../../../util';

export interface FontItem {
  bold: string;
  boldItalic: string;
  italic: string;
  regular: string;
}

export type FontMap = Map<AlineFontName, FontItem>;

export function createPDFFromJSON(
  json: DocParts,
  options: { fontMap: FontMap; imageMap: ImageMap }
): jsPDF {
  const { fontMap, imageMap } = options;

  /**
   * For convenience, configure jsPDF to use pixels as the default unit. The 'px_scaling' hotfix is
   * required because jsPDF initially implemented the 'px' unit incorrectly, and for backwards
   * compatibility the correct behavior must be explicitly enabled. Pixels are defined in jsPDF as
   * 1/96 of an inch, which matches the definition we use in Aline.
   */
  const pdf = new jsPDF({ hotfixes: ['px_scaling'], unit: 'px' });
  const links = new PDFLinks(pdf);

  /**
   * allowFsRead is a security feature to allowlist the specific directories needed, which
   * prevents malicious traversal.
   */
  pdf.allowFsRead = [];

  loadPDFFonts(pdf, { fontMap });

  const editor = AlineTransforms.createServerEditorFromJson(json, {
    overrides: {
      /**
       * The `canvas` package we use reports incorrect/smaller text width in Node, causing PDFs to come out incorrect in Node.
       * Use jsPDF's internal `getTextWidth` when producing PDFs so we have consitent results between Browser and Node.
       * See https://github.com/Automattic/node-canvas/issues/331.
       */
      measureText: (_editor, { font, text, textNode }) => {
        setPDFTextFont(pdf, font ?? Queries.getLeafFontStyle(textNode));
        return pdf.getTextWidth(text ?? textNode.text);
      },
      /** jsPDF has no way to enable OpenType features, so lay out small caps the way we draw them. */
      rendersOpenTypeFeatures: false,
    },
    state: {
      editingUserId: null,
      isInSigningCeremony: false,
      isReadOnly: true,
      isSigned: false,
      userDeclinedSigning: false,
    },
  });

  // Delete the initial page since we call addPage for each page
  pdf.deletePage(1);

  const rMDoc = buildRenderModel(editor);
  for (const [pageIndex, rMPage] of arrayEntries(rMDoc.children)) {
    const rMPageEntry: NodeEntry<RenderModelPage> = [rMPage, [0, pageIndex]];
    createPDFPage(editor, pdf, rMPageEntry, { imageMap, links });
  }

  links.createLinks();

  return pdf;
}
